import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { requestProfile } from '@/features/profiler';
import { isCatalogRow, isDatasetFacts } from '@/shared/api/generated/catalog';
import type { CatalogRow, DatasetFacts } from '@/shared/api/generated/catalog';
import { getPage } from '@/shared/api/http';

import { requestClassification } from './catalog';
import {
  BULK_CONCURRENCY,
  MAX_BULK_DATASETS,
  bulkProgress,
  planBulkAnalysis,
} from '../model/bulkAnalysis';
import type {
  AnalysisKind,
  BulkPlan,
  BulkProgress,
  TaskOutcome,
} from '../model/bulkAnalysis';

/* Running the two existing per-dataset actions across many datasets.
 *
 * **No new endpoint, and none is needed.** `POST /profile` and `POST /classify` are per-dataset and
 * both already persist their result where this screen reads it — `last_profiled_at` on the facts
 * read, `classified` on the catalogue row. A bulk endpoint would be a second server-side path to
 * the same two services for no property the client cannot hold: what the server would add is a
 * `SourceLoadGate` admission, and CLAUDE.md §9 records that as missing from the *single*-dataset
 * profile path too, so a bulk route would not have inherited it either.
 *
 * **Requests go through `requestProfile` and `requestClassification`**, the same functions
 * `useRequestProfile` and `useRequestClassification` call. So the URL, the idempotency scope and the
 * key lifecycle exist once. That is the whole reason those two were extracted.
 *
 * **Not a `useMutation`.** A mutation models one request with one outcome; this is N requests whose
 * interesting answer is a *breakdown* — how many ran, how many were already done, which failed. A
 * mutation would have to carry that in `data` anyway, and `isPending` would go false at the first
 * settle. The queries this invalidates are the ordinary ones, so the rest of the screen refreshes
 * exactly as it does after an individual run.
 */

/** How many datasets one enumerate-and-annotate round trip covers.
 *
 * 200 because that is *both* the catalogue listing's maximum page and `MAX_FACT_DATASETS`, the cap
 * on how many datasets one profile-facts request may ask about. They are equal on purpose — the
 * facts route exists to annotate a page of the listing — so one page of ids is exactly one facts
 * request. A larger chunk would be refused by the server; a smaller one would issue two requests to
 * answer one page.
 */
const CANDIDATE_PAGE = 200;

/** How often, and how many times, the facts are re-read after a bulk *profile* run.
 *
 * Five reads five seconds apart. The numbers are chosen against what they are watching rather than
 * for a feel: `POST /profile` is a `202` and the scan runs on the Temporal worker, which this
 * platform bounds to `ACTIVITY_CONCURRENCY` activities at a time — so a run of eighteen profiles
 * lands in batches over tens of seconds, and a reader watching the Last profiled column wants to
 * see the first few arrive.
 *
 * Deliberately short of "until they are all done", which this screen cannot know: a profile of a
 * large table outlasts any window worth polling for. The completion message says the work
 * continues in the background, so stopping is honest rather than a gap.
 */
const SETTLE_READS = 5;
const SETTLE_INTERVAL_MS = 5000;

/** Every dataset of one source, with the profile facts needed to decide what to skip.
 *
 * **Enumerated rather than taken from the page on screen.** The control says it covers all of this
 * source's datasets, and the table shows one page of fifty — so planning from what is rendered
 * would quietly cover the first fifty and report that it had covered everything. It walks
 * `/datasets/all`, the enumerable listing D-05 added, with the same `connection_id` and `search`
 * the screen is filtered by: a reader who has searched means the datasets they can see.
 *
 * **Facts are fetched per page rather than for the whole walk**, because the ids go in the query
 * string: two thousand UUIDs is a ~96 KB URL that a server is entitled to reject. One facts request
 * per listing page keeps every URL the size the existing screens already send.
 *
 * **A facts read that fails returns `undefined` for the whole map, not a short one**, and the
 * distinction is the reason this reports it at all. An empty map is an *answer* — "none of these
 * has been profiled" — and the planner correctly plans a profile for every row in it. A failed
 * request is not that answer, and conflating the two is how a transient 500 on the facts route
 * turns into twenty full table scans per dataset for an estate profiled yesterday. Found by a test
 * asserting the skip, which counted one profile too many.
 *
 * Coarse on purpose: one failed page cancels the profile half of the whole run rather than only
 * its own page. Tracking which ids were successfully asked about would buy a run that profiles
 * most of an estate and silently skips one page — where this reports zero profiles, which a reader
 * sees in the confirmation before agreeing to anything.
 */
export async function fetchBulkCandidates(
  connectionId: string,
  search: string,
): Promise<{
  readonly rows: readonly CatalogRow[];
  readonly facts: Map<string, DatasetFacts> | undefined;
}> {
  const rows: CatalogRow[] = [];
  const facts = new Map<string, DatasetFacts>();
  let factsRead = true;
  let cursor: string | null = null;

  while (rows.length < MAX_BULK_DATASETS) {
    const params = new URLSearchParams({
      limit: String(CANDIDATE_PAGE),
      connection_id: connectionId,
    });
    if (search.trim() !== '') params.set('search', search.trim());
    if (cursor !== null) params.set('after', cursor);

    const page = await getPage(
      `/v1/catalog/datasets/all?${params.toString()}`,
      isCatalogRow,
      'catalog row',
    );
    rows.push(...page.items);

    const ids = page.items.map((row) => row.id);
    if (ids.length > 0) {
      const factParams = new URLSearchParams();
      for (const id of ids) factParams.append('dataset_id', id);
      try {
        const factPage = await getPage(
          `/v1/catalog/datasets/facts?${factParams.toString()}`,
          isDatasetFacts,
          'dataset facts',
        );
        for (const entry of factPage.items) facts.set(entry.dataset_id, entry);
      } catch {
        /* Recorded as *unread* rather than left as a gap in the map. A gap reads as "never
           profiled", which is the one reading that spends scans on a guess. */
        factsRead = false;
      }
    }

    cursor = page.nextCursor;
    if (cursor === null) break;
  }

  return {
    rows: rows.slice(0, MAX_BULK_DATASETS),
    facts: factsRead ? facts : undefined,
  };
}

/** The plan for one source, read fresh at the moment the reader asks.
 *
 * Read on demand rather than held in a query, because the answer decides whether to spend table
 * scans and must reflect the estate *now* — a cached plan from before an individual profile ran
 * would re-request work that has since completed.
 */
export async function planFor(
  connectionId: string,
  search: string,
  kind: AnalysisKind,
): Promise<BulkPlan> {
  const { rows, facts } = await fetchBulkCandidates(connectionId, search);
  return planBulkAnalysis(rows, facts, kind);
}

export interface BulkRun extends BulkProgress {
  /**
   * Which action this run is, or `null` before the first one.
   *
   * **The state carries the kind rather than a bare `running` boolean**, and that is what lets the
   * two buttons report themselves independently: Run Profile shows "Profiling…" while Classify PII
   * stays pressable, because each asks whether *its own* kind is the one in flight. A shared
   * boolean would disable both, which would mean a reader who started a profile could not classify
   * until it finished — for two operations that do not contend with each other in this platform.
   */
  readonly kind: AnalysisKind | null;
  /** True from the moment a run starts until every task has settled. */
  readonly running: boolean;
  /** True once a run has been started in this mounting, so a completed run can report itself. */
  readonly started: boolean;
}

export interface BulkAnalysis extends BulkRun {
  readonly start: (plan: BulkPlan) => void;
}

const IDLE: BulkRun = {
  kind: null,
  total: 0,
  done: 0,
  failed: 0,
  outstanding: 0,
  finished: false,
  running: false,
  started: false,
};

/** Runs a planned set of tasks, `BULK_CONCURRENCY` at a time, and reports the breakdown.
 *
 * **A failed task never stops the others.** Each is awaited inside its own `try`, so a dataset the
 * caller cannot profile — a permission refusal, a source that has gone away since the listing was
 * read, a network fault — costs that one task and is counted in `failed`. The alternative is one
 * refusal abandoning the remaining work, which on a 32-table source means the reader retries the
 * whole run to reach the last two tables.
 *
 * **Profiling and classifying are separate tasks rather than a pair per dataset**, and that is what
 * makes "profile succeeded, classification failed" a partial success rather than a dataset-shaped
 * failure. It also means the two never block each other: a classification does not wait for the
 * twenty scans beside it.
 */
export function useBulkAnalysis(): BulkAnalysis {
  const queryClient = useQueryClient();
  const [run, setRun] = useState<BulkRun>(IDLE);
  /* Guards a second run while one is in flight — the duplicate-request bound. `useRef` rather than
     reading `run.running`, because `start` is a callback and would otherwise close over a stale
     value on the render that began the run. */
  const inFlight = useRef(false);
  /* Set on unmount. A run keeps going — the requests are already sent and the server is already
     working, so abandoning them would leave a profile running with nothing recorded — but nothing
     calls `setState` afterwards, which is the "user navigates away while processing" case. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const start = useCallback(
    (plan: BulkPlan) => {
      /* **One run at a time across both actions**, and that is the request bound rather than a UI
         preference: two runs would put eight requests in flight against a source
         `BULK_CONCURRENCY` exists to bound at four. The *buttons* stay independent — each reads
         `run.kind` — so a reader is told which action is holding the other up rather than finding
         a dead control. */
      if (inFlight.current) return;
      const tasks = plan.tasks;
      /* An empty plan is a finished run, not a started one: there is nothing to wait for, and the
         reader should be told everything was already done rather than watch a spinner settle. */
      inFlight.current = true;
      setRun({
        ...bulkProgress(tasks.length, []),
        kind: plan.kind,
        running: tasks.length > 0,
        started: true,
      });
      if (tasks.length === 0) {
        inFlight.current = false;
        return;
      }

      const outcomes: TaskOutcome[] = [];
      const touched = new Set<string>();
      let next = 0;

      const record = (outcome: TaskOutcome): void => {
        outcomes.push(outcome);
        if (mounted.current) {
          setRun({
            ...bulkProgress(tasks.length, outcomes),
            kind: plan.kind,
            running: true,
            started: true,
          });
        }
      };

      const worker = async (): Promise<void> => {
        for (;;) {
          const index = next;
          next += 1;
          const task = tasks[index];
          if (task === undefined) return;
          try {
            await (task.kind === 'profile'
              ? requestProfile(task.datasetId)
              : requestClassification(task.datasetId));
            touched.add(task.datasetId);
            record('done');
          } catch {
            /* Swallowed on purpose and counted, never rethrown: the breakdown *is* the error
               report for a bulk run, and one rejected promise must not abandon the queue. The
               individual actions keep their own per-dataset error surface untouched. */
            record('failed');
          }
        }
      };

      void Promise.all(
        Array.from({ length: Math.min(BULK_CONCURRENCY, tasks.length) }, worker),
      ).then(() => {
        inFlight.current = false;
        /* **Invalidated once at the end rather than per task.** These are page-level reads keyed on
           a sorted id list, so invalidating inside the loop would refetch the whole page up to
           2N times during the run. The same keys the individual actions invalidate, so the table,
           the two tabs and any open dataset detail all refresh through the paths that already
           existed. */
        void queryClient.invalidateQueries({ queryKey: ['catalog', 'datasets'] });
        void queryClient.invalidateQueries({ queryKey: ['catalog', 'dataset-facts'] });
        for (const datasetId of touched) {
          void queryClient.invalidateQueries({
            queryKey: ['catalog', 'dataset', datasetId, 'classification'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['catalog', 'sketches', datasetId],
          });
        }
        if (mounted.current) {
          setRun({
            ...bulkProgress(tasks.length, outcomes),
            kind: plan.kind,
            running: false,
            started: true,
          });
        }
        /* **A profile is not finished when its request returns.** `POST /profile` answers `202` and
           the scan runs on the Temporal worker afterwards, so the facts this screen reads are
           still changing for a while after the last request is sent. One invalidation above
           catches none of that: it fires when the profiles have barely started.

           So the facts are re-read a few times, on a bound, and the loop is what makes "run
           profiling for all of them" observable without a manual reload — the Last profiled column
           fills in as each scan lands. `classify` needs none of this because that route is
           synchronous: its response *is* the result.

           Bounded rather than a `refetchInterval`: a poll with no end is one that outlives the
           reason for it, and the honest stopping point here is unknown — a profile of a large
           table can take longer than any window this screen should keep polling for. So it stops,
           and the message already says the work continues in the background. */
        if (plan.kind === 'profile') {
          for (let attempt = 1; attempt <= SETTLE_READS; attempt += 1) {
            setTimeout(() => {
              if (!mounted.current) return;
              void queryClient.invalidateQueries({
                queryKey: ['catalog', 'dataset-facts'],
              });
            }, attempt * SETTLE_INTERVAL_MS);
          }
        }
      });
    },
    [queryClient],
  );

  return { ...run, start };
}
